import { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { ChannelTab, ChannelTabType } from '../../../../models/channels/types';

interface UseTabNavigationProps {
  topicId: string;
  activeTabType?: string;
  tabs?: ChannelTab[];
}

export const useTabNavigation = ({
  topicId,
  activeTabType,
  tabs = [],
}: UseTabNavigationProps) => {
  const history = useHistory();

  // Helper function to create URL-friendly slug from title
  const createSlug = useCallback((title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim();
  }, []);

  // Helper function to get unique tab value for URLs
  const tabToUrl = useCallback(
    (tab: ChannelTab) => {
      if (tab.tab_type === ChannelTabType.Custom && tab.id) {
        const slug = createSlug(tab.title);
        return `${tab.id}-${slug}`;
      }
      return tab.tab_type;
    },
    [createSlug]
  );

  // Helper function to parse custom page info from tab value
  const parseCustomTabValue = useCallback(
    (tabValue: string): { isCustomTab: boolean; tabId: number } => {
      // Check if it's a custom tab format: {tab_id}-{any-string}
      const customTabMatch = tabValue.match(/^(\d+)-.+$/);
      if (customTabMatch) {
        return {
          isCustomTab: true,
          tabId: parseInt(customTabMatch[1]),
        };
      }
      const tabId = tabs.find((tab) => tab.tab_type === tabValue)?.id;
      return { isCustomTab: false, tabId: tabId || 0 };
    },
    [tabs]
  );

  // Helper function to find tab by ID
  const findTabById = useCallback(
    (tabId: number) => {
      return tabs.find((tab) => tab.id === tabId);
    },
    [tabs]
  );

  // Helper function to get custom page ID from tab value or find it by tab ID
  const getCustomPageIdFromTabValue = useCallback(
    (tabValue: string) => {
      const { isCustomTab, tabId } = parseCustomTabValue(tabValue);
      if (isCustomTab && tabId) {
        const tab = findTabById(tabId);
        return tab?.custom_page_id || null;
      }
      return null;
    },
    [parseCustomTabValue, findTabById]
  );

  // Navigation function
  const navigateToTab = useCallback(
    (tabValue: string) => {
      history.replace(`/channels/${topicId}/${tabValue}`);
    },
    [topicId, history]
  );

  // Get the first tab value
  const firstTabValue = tabs.length > 0 ? tabToUrl(tabs[0]) : undefined;

  const activeTabId: number = useMemo(() => {
    return activeTabType
      ? parseCustomTabValue(activeTabType).tabId
      : tabs[0]?.id;
  }, [activeTabType, tabs, parseCustomTabValue]);

  // URL validation and correction effect
  useEffect(() => {
    if (activeTabType !== undefined && tabs.length > 0) {
      // Check if it's a custom tab that needs URL correction
      const { isCustomTab, tabId } = parseCustomTabValue(activeTabType);
      if (isCustomTab && tabId) {
        const tab = findTabById(tabId);
        if (tab) {
          const correctTabValue = tabToUrl(tab);
          // If the current URL doesn't match the correct format, redirect
          if (activeTabType !== correctTabValue) {
            history.replace(`/channels/${topicId}/${correctTabValue}`);
            return;
          }
        } else {
          // Tab ID not found, redirect to first tab
          if (firstTabValue) {
            history.replace(`/channels/${topicId}/${firstTabValue}`);
          } else {
            history.replace(`/channels/${topicId}`);
          }
          return;
        }
      } else {
        // Check if it's a valid predefined tab
        const validTabValues = tabs.map(tabToUrl);
        if (!validTabValues.includes(activeTabType)) {
          // Invalid tab, redirect to first tab or home
          if (firstTabValue) {
            history.replace(`/channels/${topicId}/${firstTabValue}`);
          } else {
            history.replace(`/channels/${topicId}`);
          }
          return;
        }
      }
    } else if (activeTabType === undefined && firstTabValue) {
      // No tab specified, redirect to first tab
      navigateToTab(firstTabValue);
    }
  }, [
    activeTabType,
    topicId,
    firstTabValue,
    history,
    navigateToTab,
    tabs,
    parseCustomTabValue,
    findTabById,
    tabToUrl,
  ]);

  return {
    // Helper functions
    createSlug,
    tabToUrl,
    parseCustomTabValue,
    findTabById,
    getCustomPageIdFromTabValue,
    navigateToTab,
    activeTabId,
  };
};
